// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import usePrevious from "@insite/client-framework/Common/Hooks/usePrevious";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { Cart } from "@insite/client-framework/Services/CartService";
import { Session } from "@insite/client-framework/Services/SessionService";
import { SettingsModel } from "@insite/client-framework/Services/SettingsService";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import ContextState from "@insite/client-framework/Store/Context/ContextState";
import { AccountModel } from "@insite/client-framework/Types/ApiModels";
import { Option } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useGetShipToAndBillTo from "@store/Framework/Store/Data/Accounts/Hooks/useGetShipToAndBillTo";
import InfosBox, { InfosBoxStyles } from "@store/Library/Components/Toolkit/InfosBox";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useCurrentCart } from "@toolkit/Framework/Store/Data/Carts/CartsHooks";
import get from "lodash/get";
import React from "react";
import { css } from "styled-components";

type Properties = "cart" | "settings" | "session" | "customer" | "account" | "context";
interface ComparisonProperties {
    cart: Readonly<Cart> | undefined;
    settings: SettingsModel;
    session: Session;
    customer: ReturnType<typeof useGetShipToAndBillTo>;
    account: Readonly<AccountModel> | undefined;
    context: ContextState;
}

type ComparisonOperations =
    | "equals"
    | "notEquals"
    | "greaterThan"
    | "lessThan"
    | "contains"
    | "isEmpty"
    | "isNotEmpty"
    | "notContains";

enum fields {
    propertyType = "propertyType",
    propertyPath = "propertyPath",
    comparisonOperator = "comparisonOperator",
    comparisonValue = "comparisonValue",
    debugMode = "debugMode",
    comparisons = "comparisons",
}

type PropertyValue = string | number | any[] | null | undefined;

interface Comparison {
    propertyType: Properties;
    propertyPath: string;
    comparisonOperator: ComparisonOperations;
    comparisonValue: string;
}
type ComparisonHandler = (value: PropertyValue, comparisonValue: string) => boolean;

interface PropertyZonesProps extends WidgetProps {
    fields: {
        [fields.comparisons]: { fields: Comparison }[];
        [fields.debugMode]: boolean;
    };
}

interface PropertyZonesStyles {
    wrapper?: InjectableCss;
    debugWrapper?: InjectableCss;
    comparisonWrapper?: InjectableCss;
    singlePropertyInfosBox?: InfosBoxStyles;
    allValidInfosBox?: InfosBoxStyles;
}

const propertyZonesStyles: PropertyZonesStyles = {
    debugWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    comparisonWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(8)};
            flex-wrap: wrap;
        `,
    },
    singlePropertyInfosBox: {
        wrapper: {
            css: css`
                width: fit-content;
            `,
        },
    },
    allValidInfosBox: {
        wrapper: {
            css: css`
                justify-content: center;
                width: fit-content;
                margin-inline: auto;
            `,
        },
        content: {
            css: css`
                width: fit-content;
            `,
        },
    },
};

const comparisonHandlers: Record<ComparisonOperations, ComparisonHandler> = {
    equals: (value: PropertyValue, comparisonValue: string) => value?.toString() === comparisonValue,
    notEquals: (value: PropertyValue, comparisonValue: string) => value?.toString() !== comparisonValue,
    greaterThan: (value: PropertyValue, comparisonValue: string) => {
        const numValue = typeof value === "number" ? value : Number(value);
        if (Number.isNaN(numValue)) {
            return false;
        }
        const compareValue = Number(comparisonValue);
        if (Number.isNaN(compareValue)) {
            return false;
        }
        return numValue > compareValue;
    },
    lessThan: (value: PropertyValue, comparisonValue: string) => {
        const numValue = typeof value === "number" ? value : Number(value);
        if (Number.isNaN(numValue)) {
            return false;
        }
        const compareValue = Number(comparisonValue);
        if (Number.isNaN(compareValue)) {
            return false;
        }
        return numValue < compareValue;
    },
    contains: (value: PropertyValue, comparisonValue: string) => {
        if (Array.isArray(value) || typeof value === "string") {
            return value.includes(comparisonValue);
        }
        return false;
    },
    notContains: (value: PropertyValue, comparisonValue: string) => {
        if (Array.isArray(value) || typeof value === "string") {
            return !value.includes(comparisonValue);
        }
        return false;
    },
    isEmpty: (value: PropertyValue) => {
        if (!value) {
            return true;
        }
        if (Array.isArray(value) || typeof value === "string") {
            return value.length === 0;
        }
        return false;
    },
    isNotEmpty: (value: PropertyValue) => {
        if (!value) {
            return false;
        }
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return true;
    },
};

const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
};

const evaluateComparison = (comparison: Comparison, properties: ComparisonProperties) => {
    const { propertyType, propertyPath, comparisonOperator, comparisonValue } = comparison;
    const property = properties[propertyType];
    const value: PropertyValue = get(property, propertyPath, null);
    const isValid = comparisonHandlers[comparisonOperator]
        ? comparisonHandlers[comparisonOperator](value ?? "", comparisonValue)
        : false;

    return { propertyType, propertyPath, value, comparisonOperator, comparisonValue, isValid };
};
const PropertyZones: React.FC<PropertyZonesProps> = ({ id, fields: { comparisons, debugMode } }) => {
    const isEditing = useShellContext().isEditing;
    const account = useAppSelector(getCurrentAccountState)?.value;

    const context = useAppSelector(state => state.context);
    const { session, settings } = context;
    const cart = useCurrentCart();
    const customer = useGetShipToAndBillTo();
    const previousComparisons = usePrevious(comparisons);
    const properties: ComparisonProperties = { cart, settings, session, customer, account, context };

    const changedPropertyType: Properties | null = comparisons?.reduce(
        (acc, comparison, index) => {
            if (comparisons.length !== previousComparisons?.length || !previousComparisons) {
                return null;
            }
            const previousComparison = previousComparisons?.[index]?.fields.propertyType;
            const currentComparison = comparison.fields.propertyType;
            if (previousComparison !== currentComparison) {
                return currentComparison;
            }
            return acc;
        },
        null as Properties | null,
    );
    if (changedPropertyType) {
        // We're logging the changed property type and its value for debugging purposes in the CMS
        // This allows the CMS user to better debug their property zones
        console.log(capitalizeFirstLetter(changedPropertyType), properties[changedPropertyType]);
    }

    const allValid =
        comparisons?.length > 0 &&
        comparisons.every(comparison => evaluateComparison(comparison.fields, properties).isValid);

    const showZone = allValid || isEditing;
    return (
        <StyledWrapper {...propertyZonesStyles.wrapper}>
            {debugMode && (
                <StyledWrapper {...propertyZonesStyles.debugWrapper}>
                    {comparisons?.map((comparison, index) => {
                        const { comparisonOperator, comparisonValue, isValid, propertyPath, propertyType, value } =
                            evaluateComparison(comparison.fields, properties);
                        return (
                            <StyledWrapper {...propertyZonesStyles.comparisonWrapper} key={index}>
                                {`Property Type: ${capitalizeFirstLetter(
                                    propertyType,
                                )}, Property Path: ${propertyPath}, Property Value: ${value}, Comparison: ${comparisonOperator}, Comparison Value: ${comparisonValue}`}
                                <InfosBox
                                    size="tiny"
                                    type={isValid ? "success" : "error"}
                                    extendedStyles={propertyZonesStyles.singlePropertyInfosBox}
                                >{`isValid: ${isValid}`}</InfosBox>
                            </StyledWrapper>
                        );
                    })}
                    <InfosBox
                        extendedStyles={propertyZonesStyles.allValidInfosBox}
                        size="tiny"
                        type={allValid ? "success" : "error"}
                    >{`All Valid: ${allValid}`}</InfosBox>
                </StyledWrapper>
            )}

            {showZone && <Zone contentId={id} zoneName="zone" />}
        </StyledWrapper>
    );
};

const propertyOptions: Option<Properties>[] = [
    { value: "cart", displayName: "Cart" },
    { value: "settings", displayName: "Settings" },
    { value: "session", displayName: "Session" },
    { value: "customer", displayName: "Customer" },
    { value: "account", displayName: "Account" },
    { value: "context", displayName: "Context" },
];
const comparisonOptions: Option<ComparisonOperations>[] = [
    { value: "isEmpty", displayName: "Is Empty" },
    { value: "isNotEmpty", displayName: "Is Not Empty" },
    { value: "notContains", displayName: "Not Contains" },
    { value: "contains", displayName: "Contains" },
    { value: "equals", displayName: "Equals" },
    { value: "notEquals", displayName: "Not Equals" },
    { value: "greaterThan", displayName: "Greater Than" },
    { value: "lessThan", displayName: "Less Than" },
];
const widgetModule: WidgetModule<Bunzl.BunzlDefinition> = {
    component: PropertyZones,
    definition: {
        canAdd: context => context.permissions?.canUseAdvancedFeatures ?? false,
        group: "Common",
        displayName: "Property Zones",
        fieldDefinitions: [
            {
                name: fields.comparisons,
                editorTemplate: "ListField",

                fieldDefinitions: [
                    {
                        name: fields.propertyType,
                        editorTemplate: "DropDownField",
                        defaultValue: "settings",
                        isRequired: true,
                        options: propertyOptions,
                    },
                    {
                        name: fields.propertyPath,
                        editorTemplate: "PropertyPathPickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: fields.comparisonOperator,
                        editorTemplate: "DropDownField",
                        defaultValue: "equals",
                        isRequired: true,
                        options: comparisonOptions,
                    },
                    {
                        name: fields.comparisonValue,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],

                fieldType: "Contextual",
                isRequired: false,
                defaultValue: [],
                getDisplay: ({ fields: { propertyType, propertyPath } }) => {
                    return `${propertyType} - ${propertyPath}`;
                },
            },
            {
                name: fields.debugMode,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                isRequired: false,
                fieldType: "General",
            },
        ],
    },
};
export default widgetModule;
